// Packages
import React from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

// Compomonets
import SEO from "@components/global/seo"

// Styles
const size = {
  tablet: 850,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: 856px;
  padding: 24px 48px 120px;
  ${below.mobile`padding: 24px 24px 80px`}

  h1 {
    margin-bottom: 8px;
  }
  h4 {
    font-size: 24px;
    line-height: 32px;
    color: black;
  }
  h5 {
    font-family: "Playfair Display", serif;
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;
    margin-bottom: 24px;
  }
  ul {
    margin: 8px 0;
    li {
      :before {
        top: 16px;
      }
    }
  }
  p {
    font-size: 18px;
    line-height: 24px;
    ${below.mobile`font-size: 16px;`}
  }
  hr {
    width: 100%;
    border: none;
    padding-top: 1px;
    height: 1px;
    border-bottom: 1px solid #75808f;
  }

  td {
    border-bottom: none;
    padding: 16px;
    font-size: 20px;
    line-height: 32px;
    a {
      color: var(--pacific-blue-hex);
    }
    ${below.mobile`padding: 8px`}
    :first-child {
      font-weight: 400;
      padding-left: 2rem;
    }
  }
  th {
    padding: 16px;
    ${below.mobile`padding: 8px`}
    background-color: #264564;
    color: white;
    font-weight: 400;
    border-bottom: none;
    text-align: center;
    font-size: 20px;
    line-height: 28px;
  }
  tr:nth-child(even) {
    background-color: #eff2f5;
  }
  strong {
    color: #264564;
  }
  table {
    line-height: 3rem;
    margin: 64px 0;
    font-size: 18px;
    line-height: 24px;
    ${below.mobile`font-size: 16px;`}
  }
`

const PrivacyPolicy = props => {
  return (
    <>
      <Wrapper>
        <h1>Privacy Policy</h1>

        <table>
          <thead>
            <tr>
              <th style={{ width: "15%" }}>Facts</th>
              <th style={{ width: "85%" }}>
                What does Atlantic Bay Mortgage Group and its family of
                companies do with your personal information?
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Why?</strong>
              </td>
              <td>
                Financial companies choose how they share your personal
                information. Federal law gives consumers the right to limit some
                but not all sharing. Federal law also requires us to tell you
                how we collect, share, and protect your personal information.
                Please read this notice carefully to understand what we do.
              </td>
            </tr>
            <tr>
              <td>
                <strong>What?</strong>
              </td>
              <td>
                The types of personal information we collect, and share depend
                on the product or service you have with us. This information can
                include:
                <ul>
                  <li>Social Security number and account balances</li>
                  <li>Credit history and credit scores</li>
                  <li>Employment information and payment history</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                <strong>How?</strong>
              </td>
              <td>
                All financial companies need to share customers' personal
                information to run their everyday business. In the section
                below, we list the reasons financial companies can share their
                customers' personal information; the reasons Atlantic Bay
                Mortgage Group and its family of companies chooses to share; and
                whether you can limit this sharing.
              </td>
            </tr>
          </tbody>
        </table>
        <strong>
          All the below categories exclude text messaging originator opt-in data
          and consent; this information will not be shared with any third
          parties.
        </strong>
        <table>
          <thead>
            <tr>
              <th style={{ width: "34%" }}>
                Reasons we can share your personal information
              </th>
              <th style={{ width: "33%" }}>
                Does Atlantic Bay Mortgage Group and its family of companies
                share?
              </th>
              <th style={{ width: "33%" }}>Can you limit this sharing?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>For our everyday business purposes</strong> – such as to
                process your transactions, maintain your account(s), respond to
                court orders and legal investigations, or report to credit
                bureaus
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>No</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>For our marketing purposes</strong> – to offer our
                products and services to you
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>No</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>
                  For joint marketing with other financial companies
                </strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>No</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>For our affiliates' everyday business purposes</strong>{" "}
                – information about your transactions and experiences
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>For our affiliates' everyday business purposes</strong>{" "}
                – information about your creditworthiness
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>For our affiliates to market to you</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
            </tr>
            <tr>
              <td>
                <strong>For nonaffiliates to market to you</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
              <td style={{ textAlign: "center" }}>
                <strong>Yes</strong>
              </td>
            </tr>
            <tr>
              <td>To limit our sharing</td>
              <td colSpan="2">
                Use our <strong>online form</strong> (
                <Link
                  to={`/${
                    props.bankerData ? `${props.bankerData.slug}/` : ""
                  }opt-out`}
                >
                  www.atlanticbay.com/
                  {props.bankerData ? `${props.bankerData.slug}/` : ""}opt-out
                </Link>
                ) and our menu will prompt you through your choice(s).
                <br />
                <strong>Please note:</strong> If you are a new customer, we can
                begin sharing your information thirty (30) days from the date we
                sent this notice. When you are no longer our customer, we
                continue to share your information as described in this notice.
                <br />
                However, you can contact us at any time to limit our sharing.
              </td>
            </tr>
            <tr>
              <td>Questions?</td>
              <td colSpan="2">
                Use our <strong>online form </strong>(
                <Link
                  to={`/${
                    props.bankerData ? `${props.bankerData.slug}/` : ""
                  }opt-out`}
                >
                  www.atlanticbay.com/
                  {props.bankerData ? `${props.bankerData.slug}/` : ""}opt-out
                </Link>
                )
              </td>
            </tr>
          </tbody>
        </table>

        <div>
          <table width="100%">
            <thead>
              <tr>
                <th style={{ width: "75%" }} colSpan="2">
                  Who we are
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Who is providing this notice?</strong>
                </td>
                <td>
                  This notice is provided by Atlantic Bay Mortgage Group and its
                  family of companies, as listed in the section below titled,
                  'Other important information'.
                </td>
              </tr>
            </tbody>
          </table>
          <table width="100%">
            <thead>
              <tr>
                <th style={{ width: "75%" }} colSpan="2">
                  What we do
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>
                    How does Atlantic Bay and its family of companies protect my
                    personal information?
                  </strong>
                </td>
                <td>
                  To protect your personal information from unauthorized access
                  and use, we use security measures that comply with federal
                  law. These measures include computer safeguards and secured
                  files and buildings. Our employees are committed to
                  safeguarding customer information and preventing its
                  unauthorized access, disclosure, or use. In accessing your
                  personal information, our employees are bound by the Code of
                  Ethics Policy and our information security standards.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>
                    How does Atlantic Bay and its family of companies collect my
                    personal information?
                  </strong>
                </td>
                <td>
                  We collect your personal information, for example, when you:
                  <ul>
                    <li>apply for a loan or deposit money</li>
                    <li>open an account or pay your bills</li>
                    <li>provide account information</li>
                  </ul>
                  We also collect your personal information from others, such as
                  credit bureaus, affiliates or other companies.
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Why can’t I limit all sharing?</strong>
                </td>
                <td>
                  Federal law gives you the right to limit only:
                  <ul>
                    <li>
                      sharing for affiliates' everyday business
                      purposes—information about your creditworthiness
                    </li>
                    <li>
                      affiliates from using your information to market to you
                    </li>
                    <li>sharing for nonaffiliates to market to you</li>
                  </ul>
                  State laws and individual companies may give you additional
                  rights to limit sharing. [See below for more on your rights
                  under state law.]
                </td>
              </tr>
              <tr>
                <td>
                  <strong>
                    What happens when I limit sharing for an account I hold
                    jointly with someone else?
                  </strong>
                </td>
                <td>Your choices will apply to everyone on your account.</td>
              </tr>
            </tbody>
          </table>
          <table width="100%">
            <thead>
              <tr>
                <th style={{ width: "65%" }} colSpan="2">
                  Definitions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Affiliates</strong>
                </td>
                <td>
                  Companies related by common ownership or control. They can be
                  financial and nonfinancial companies.
                  <ul>
                    <li>
                      Our affiliates include financial companies with an
                      Atlantic Bay name and other financial companies such as
                      mortgage companies, title agencies, and insurance
                      agencies. For a list of our affiliates, see the section
                      below.
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Nonaffiliates</strong>
                </td>
                <td>
                  Companies not related by common ownership or control. They can
                  be financial and nonfinancial companies.
                  <ul>
                    <li>
                      Nonaffiliates we share with can include certain
                      information regarding your experience with Atlantic Bay,
                      such as, name, address, phone number, and email address.
                      This information is shared with non-affiliates with whom
                      we have a formal relationship to offer certain home
                      ownership services to you. At no time will we share your
                      social security number or loan information.
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Joint Marketing</strong>
                </td>
                <td>
                  A formal agreement between nonaffiliated financial companies
                  that together market financial products or services to you:
                  <ul>
                    <li>
                      Our joint marketing partners include financial companies
                      that may provide products or services on our behalf.
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
          <table width="100%" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Other important information</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    width: "100%",
                    textAlign: "center",
                    letterSpacing: ".3px",
                    padding: "24px 24px 0",
                  }}
                >
                  <p>
                    Atlantic Bay Mortgage Group, L.L.C.; Mullinix Mortgage
                    Group; AB Insurance LLC, Atlantic Carolinas Title LLC, and
                    AB Title LLC; Gray Fox, LLC
                  </p>
                  <p>
                    State and Federal Regulations: In addition to federal law,
                    you may have other privacy protections under some state
                    laws. We will comply with all applicable state laws as to
                    information about you.
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Wrapper>
    </>
  )
}

// Meta tags
export const Head = props => {
  const canonicalSlug = props.pageContext.canonicalSlug
  return (
    <SEO
      description={`We value your business and your privacy, learn more about our privacy policies.`}
      title={`Privacy Policy | Atlantic Bay Mortgage Group®`}
      canonicalSlug={canonicalSlug}
      slug={props.location.pathname.replace(/^\//, "").replace(/\/$/, "")}
    />
  )
}

export default PrivacyPolicy
